import { emitMapsLifecycle } from "@/lib/maps-lifecycle-bus";

/**
 * Dev-mode runtime audit for Google Places Autocomplete instances.
 *
 * Wraps an Autocomplete instance's `addListener` method so that whenever
 * a `place_changed` handler is registered we count the live (un-removed)
 * listeners for that event on that specific instance. If a second one is
 * ever attached we:
 *
 *   1. `console.warn` with a clear message (no-op in production).
 *   2. Emit a lifecycle bus event so the dev panel surfaces it.
 *   3. Proactively remove the duplicate by returning a token whose
 *      removal happens immediately — the caller still gets a valid
 *      MapsEventListener back so existing cleanup code keeps working,
 *      but the duplicate handler is detached from the underlying
 *      instance before it can fire.
 *
 * In production builds (`!import.meta.env.DEV`) this function is a no-op
 * and the original instance is returned untouched.
 */
export function auditAutocompleteInstance<T extends { addListener: (type: string, cb: (...args: any[]) => void) => any }>(
  ac: T,
  context = "AddressAutocomplete",
): T {
  if (!import.meta.env.DEV) return ac;
  if ((ac as any).__lovableAudited) return ac;

  // Per-event live-listener registry keyed by event type.
  const live = new Map<string, Set<any>>();

  const originalAddListener = ac.addListener.bind(ac);

  (ac as any).addListener = (type: string, cb: (...args: any[]) => void) => {
    const token = originalAddListener(type, cb);

    // Track place_changed specifically — that's the invariant we care
    // about most. Generic tracking would also catch other duplicates,
    // but we keep the noise low here on purpose.
    if (type === "place_changed") {
      const set = live.get(type) ?? new Set<any>();
      set.add(token);
      live.set(type, set);

      if (set.size > 1) {
        const message =
          `[${context}] Duplicate '${type}' listener detected ` +
          `(now ${set.size} live on this Autocomplete instance). ` +
          `Removing the new one to enforce single-handler invariant.`;
        // eslint-disable-next-line no-console
        console.warn(message);
        emitMapsLifecycle(
          "autocomplete",
          "listener:remove",
          `duplicate ${type} auto-removed (was ${set.size}× live)`,
        );

        // Detach the just-added duplicate via the global event registry.
        try {
          const gEvent = (window as any).google?.maps?.event;
          if (gEvent?.removeListener) {
            gEvent.removeListener(token);
          } else if (typeof token?.remove === "function") {
            // Newer Google Maps tokens expose `.remove()` directly.
            token.remove();
          }
        } catch {
          /* ignore — best-effort */
        }
        set.delete(token);

        // Return the still-live original token (the first one) so the
        // caller's cleanup code can remove *that* one on unmount.
        const first = set.values().next().value;
        if (first) return first;
      }

      // Wrap the token so removal also updates our local registry.
      return wrapToken(token, () => set.delete(token));
    }

    return token;
  };

  Object.defineProperty(ac, "__lovableAudited", {
    value: true,
    enumerable: false,
    writable: false,
  });
  return ac;
}

function wrapToken(token: any, onRemove: () => void) {
  // We wrap the listener token so that if anyone calls `token.remove()`
  // directly, our registry stays in sync. The global
  // `google.maps.event.removeListener(token)` path doesn't go through
  // this wrapper, but that's fine — duplicate detection only needs to
  // be conservative (we'd over-warn at worst, never under-warn).
  if (token && typeof token === "object" && typeof token.remove === "function") {
    const origRemove = token.remove.bind(token);
    token.remove = () => {
      try { origRemove(); } finally { onRemove(); }
    };
  }
  return token;
}
